import { Direction } from '@angular/cdk/bidi';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import _assign from 'lodash/assign';
import _defaultsDeep from 'lodash/defaultsDeep';
import { AuditLogRecordDto } from '@portal/models/auditLogRecordDto';
import { PromocodePaginationRequest } from '@portal/models/promocodePaginationRequest';
import { SkuResponse } from '@portal/models/skuResponse';
import {
  AppType,
  BuilderSettings,
  CurrencySettings,
  LanguageLocale,
  LogoConfig,
  PhoneSettings,
  SignUpProfileType,
  Theme,
} from '../builder.interface';
import { builderEnvironment } from '../environment';
import { Locale } from './builder-info.constants';
import CurrencyEnum = SkuResponse.CurrencyEnum;
import LangEnum = PromocodePaginationRequest.LangEnum;
import ProfileTypeEnum = AuditLogRecordDto.ProfileTypeEnum;

@Injectable({ providedIn: 'root' })
export class BuilderInfoService {
  private readonly builderInfo = builderEnvironment;

  private readonly localeCashed: Locale;

  get settings(): BuilderSettings {
    return this.setting.getValue();
  }

  get locale(): Locale {
    return this.localeCashed;
  }

  get currency(): CurrencyEnum {
    return this.setting.getValue().currency;
  }

  get currencySymbol(): string {
    return this.setting.getValue().currencySymbol;
  }

  get availableCurrency(): CurrencyEnum[] {
    const currencies = this.setting.getValue().currencySettings?.map(currency => currency.currency);

    return currencies?.length ? currencies : Object.values(CurrencyEnum);
  }

  get availableLanguages(): LangEnum[] {
    return this.setting.getValue().langSetting?.availableLangs;
  }

  get currencySettings(): CurrencySettings {
    return this.setting.getValue().currencySettings.find(s => s.active);
  }

  get lang(): LangEnum {
    return this.setting.getValue().langSetting.defaultLang;
  }

  get availableLangs(): LangEnum[] {
    return this.setting.getValue().langSetting.availableLangs;
  }

  get appType(): AppType {
    return this.builderInfo.appType;
  }

  get settlementApi(): boolean {
    return !!this.setting.getValue().settlementApi;
  }

  get phoneSettings(): PhoneSettings[] {
    return this.setting.getValue().phoneSettings;
  }

  get activePhoneSetting(): PhoneSettings[] {
    return this.setting.getValue().phoneSettings.filter(s => s.active);
  }

  get theme(): Theme {
    return this.builderInfo.theme || Theme.DEFAULT;
  }

  get sidebarLogoConfig(): LogoConfig {
    return this.builderInfo.assets.logo.sidebar;
  }

  get authBackgroundConfig(): string {
    return this.builderInfo.assets?.authBackground;
  }

  get authPageLogoConfig(): LogoConfig {
    return this.builderInfo.assets.logo.authPage;
  }

  get interfaceDirection(): Direction {
    // eslint-disable-next-line no-restricted-globals
    const localLang: string = localStorage.getItem('language') || this.builderInfo.systemLang?.toLowerCase();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    return localLang === LanguageLocale.ar ? 'rtl' : 'ltr';
  }

  get allowedSignUpProfiles(): Record<SignUpProfileType, boolean> {
    return Object.values(SignUpProfileType).reduce(
      (acc: Record<SignUpProfileType, boolean>, cur: SignUpProfileType) => ({
        ...acc,
        [cur]: !(this.settings.disabledSignUpProfiles || []).some((type: SignUpProfileType) => type === cur),
      }),
      {} as Record<SignUpProfileType, boolean>,
    );
  }

  get allowedProfiles(): Record<ProfileTypeEnum, boolean> {
    return Object.values(ProfileTypeEnum).reduce(
      (acc: Record<ProfileTypeEnum, boolean>, cur: ProfileTypeEnum) => ({
        ...acc,
        [cur]: !(this.settings.disabledProfiles || []).some((type: ProfileTypeEnum) => type === cur),
      }),
      {} as Record<ProfileTypeEnum, boolean>,
    );
  }

  private readonly setting = new BehaviorSubject<BuilderSettings>(null);

  constructor() {
    this.localeCashed = this.getLocale();
    this.setting.next(this.builderInfo);
  }

  updateSettings(settings: BuilderSettings) {
    const updated = _assign(settings, _defaultsDeep(settings, builderEnvironment));
    this.setting.next(updated);
  }

  private getLocale(): Locale {
    // eslint-disable-next-line no-restricted-globals
    const lang = localStorage.getItem('language')?.toUpperCase() || this.builderInfo.langSetting.defaultLang;
    switch (lang) {
      case LangEnum.RU:
        return Locale.RU;
      case LangEnum.EN:
        return Locale.EN;
      case LangEnum.FR:
        return Locale.FR;
      case LangEnum.AR:
        return Locale.AR;
      case LangEnum.HI:
        return Locale.HI;
      case LangEnum.KM:
        return Locale.KHR;
      case LangEnum.ZH:
        return Locale.CNY;
      default:
        return Locale.RU;
    }
  }
}
